/**
 * El catalogo interno. Es LA lista de la pantalla de Productos: las ocho
 * secciones —iPhone, Apple, Usados, Accesorios, Perfumes, Vapers, Otros y A
 * pedido— se ven todas acá, con el mismo buscador, los mismos filtros, la
 * misma densidad y las mismas columnas.
 *
 * LO QUE APORTA CADA SECCION SIN ROMPER LA LISTA
 * ----------------------------------------------
 * iPhone y Usados supieron tener vista propia (una grilla por modelo) y la
 * perdieron: eran tres pantallas distintas adentro de una, y las dos bonitas
 * eran las que no tenian ni buscador ni filtros ni costo ni stock.
 *
 * Lo que esas vistas si tenian —la seña de la unidad usada, sus chips de
 * estado, el boton de «ya lo tengo»— no se perdio: entra por `extras`, que es
 * lo unico que una seccion puede agregarle a la fila. La lista no sabe que es
 * un usado; sabe pintar una segunda linea, unos chips y una accion.
 *
 * QUE MUESTRA CADA FILA Y POR QUE
 * -------------------------------
 * Costo y precio van los DOS, cada uno con su moneda, aunque ocupen el doble.
 * Es la unica forma de ver de un vistazo si un producto quedo mal cargado: un
 * costo en dolares con un precio en pesos que no guarda ninguna relacion con el
 * salta a la vista en una tabla y es invisible en una ficha.
 *
 * El stock va al lado del precio porque la pregunta "cuanto sale" casi nunca
 * viene sola: viene con "y si hay".
 *
 * En desktop es una tabla densa. En mobile son tarjetas: siete columnas con
 * scroll horizontal en un celular no se leen, se adivinan.
 */
import { Fragment, useState, type KeyboardEvent, type ReactNode } from "react";
import { ChevronDown, ChevronRight, Pencil, PencilRuler, TriangleAlert } from "lucide-react";

import { Importe } from "@/components/money";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import { Codigo } from "@/components/ui/codigo";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { IconButton } from "@/components/ui/icon-button";
import { InputBusqueda } from "@/components/ui/input-busqueda";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableHead,
  TableHeader,
  TableFilaDetalle,
  TableRow,
  TableVacia,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { aNumero, type Producto, type Proveedor, type Rubro } from "@/lib/tipos";

import { mostrarCantidad } from "./Campos";
import { FILTROS_INICIALES, type FiltrosUi } from "./logica";
import { nombreCompleto } from "./nombre";

// ─────────────────────────────────────────────────────────────────────────────
// Marcas de un producto
// ─────────────────────────────────────────────────────────────────────────────

/**
 * "Publicado" ya no es una marca del catalogo: que se ve en la tienda se decide
 * en una sola pantalla, Tienda online, y repetirlo aca era tener dos lugares
 * para lo mismo.
 */
export type MarcaProducto = "precioManual" | "aPedido" | "dadoDeBaja";

/**
 * MASTER seccion 10, Productos: "dado de baja" es un estado terminal
 * (`secondary`), "a pedido" un estado de espera (`info`); "Precio a mano" no
 * es un estado sino una etiqueta (`outline`).
 */
export const TONO_ESTADO: Record<MarcaProducto, NonNullable<BadgeProps["variant"]>> = {
  precioManual: "outline",
  aPedido: "info",
  dadoDeBaja: "secondary",
};

export const ETIQUETA_MARCA: Record<MarcaProducto, string> = {
  precioManual: "Precio a mano",
  aPedido: "A pedido",
  dadoDeBaja: "Dado de baja",
};

/** Las marcas que lleva un producto, en el orden en que se muestran. */
export function marcasDe(p: Producto): MarcaProducto[] {
  const marcas: MarcaProducto[] = [];
  if (p.precio_manual !== null) marcas.push("precioManual");
  if (p.a_pedido) marcas.push("aPedido");
  if (!p.activo) marcas.push("dadoDeBaja");
  return marcas;
}

// ─────────────────────────────────────────────────────────────────────────────
// La lista
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lo que una seccion le agrega a la fila sin cambiar la lista.
 *
 * Son cinco cosas y nada mas que cinco, a proposito: lo que no entra en una
 * columna, un detalle desplegable, un chip, una accion o un atenuado no es un
 * detalle de seccion, es otra pantalla. Hoy las usa Usados, que es la unica
 * seccion cuyas filas son unidades fisicas y no renglones de catalogo.
 */
export interface ExtrasDeFila {
  /**
   * Columnas propias de la seccion, entre Codigo y Costo.
   *
   * Son para los atributos que la seccion mira de un vistazo y compara entre
   * filas —el color y la bateria de un usado—. Un atributo que se compara
   * quiere una columna; uno que se consulta de a uno va al detalle.
   */
  columnas?: ReadonlyArray<{
    titulo: string;
    /** `derecha` para numeros, que es como se comparan. */
    alineacion?: "izquierda" | "derecha";
    celda: (p: Producto) => ReactNode;
  }>;
  /**
   * Lo que se abre al apretar la fila. Devolver `null` deja esa fila sin
   * desplegar (un producto comun no tiene nada mas que contar).
   */
  detalle?: (p: Producto) => ReactNode;
  /** Chips propios de la seccion, despues de las marcas comunes. */
  chips?: (p: Producto) => ReactNode;
  /**
   * Que marcas comunes se ven en la FILA. El resto no desaparece: la seccion
   * las baja al detalle.
   *
   * Existe porque un chip que aparece en todas las filas no informa nada y
   * ocupa el ancho del nombre. En Usados «Precio a mano» estaba en las 29 y
   * cortaba el nombre a «IPHONE 14 128GB (US...».
   */
  marcas?: (p: Producto) => MarcaProducto[];
  /**
   * Lo que a esa ficha le FALTA, en palabras: «Falta el IMEI», «Sin precio de
   * venta». Colapsada, la fila muestra un contador; desplegada, la lista.
   *
   * Es una categoria aparte de `chips` a proposito. Un chip dice que ES el
   * producto —a pedido, vendido, referencia— y hay que verlo sin abrir nada.
   * Un pendiente es trabajo por hacer: el numero alcanza para saber que hay
   * algo, y el detalle dice que. Mezclarlos convierte cuatro estados normales
   * en cuatro alarmas.
   */
  pendientes?: (p: Producto) => string[];
  /** Una accion mas, a la izquierda del lapiz. */
  accion?: (p: Producto) => ReactNode;
  /** La fila se atenua: el registro ya no esta en juego (un usado vendido). */
  apagada?: (p: Producto) => boolean;
  /**
   * Que numero muestra la columna Stock.
   *
   * Existe para las secciones que cuelgan unidades de un modelo: la ficha del
   * modelo esta en cero —y tiene que estarlo, es la lista de precios— pero lo
   * que hay que leer recorriendo es cuantos equipos de ese modelo hay. Sin
   * esto la fila dice 0 con tres equipos adentro.
   */
  stock?: (p: Producto) => number;
}

/**
 * Una fila que NO es un producto, sino un modelo entero.
 *
 * "iPhone 17 Pro Max" no existe en la base: existen el de 256, el de 512 y el
 * de 1 TB. Pero en el mostrador el modelo es UNA cosa —lo que cambia adentro
 * es cuanta memoria y de que color— y listarlo tres veces con el nombre casi
 * igual es la misma repeticion que ya sacamos colgando las unidades.
 *
 * La lista no sabe que es un iPhone ni que es una memoria: dibuja un titulo,
 * un resumen, un numero de stock y lo que se abre. Quien arma los grupos es la
 * seccion, que si sabe.
 */
export interface GrupoDeFilas {
  clave: string;
  /** Como se llama el modelo, escrito para leer: "iPhone 17 Pro Max". */
  titulo: string;
  /** Una celda por cada columna propia de la seccion, en el mismo orden. */
  columnas?: ReadonlyArray<ReactNode>;
  /** Las unidades de todo el modelo, sumadas. */
  stock: number;
  /**
   * Lo que se abre, EN LAS MISMAS COLUMNAS: tiene que devolver `<TableRow>`s.
   *
   * El desplegable era una celda que cruzaba la tabla entera con su propio
   * orden adentro, y eso lo volvia ilegible: abajo del encabezado COLOR habia
   * un precio y abajo de COSTO un "3 u.". Una memoria es una fila de lo mismo
   * un nivel mas adentro, asi que es una fila de verdad y se alinea sola.
   *
   * Recibe el ancho de la tabla porque adentro hay cosas que SI cruzan —el
   * editor de precios, los equipos de esa memoria— y ese numero lo sabe la
   * lista, no la seccion.
   */
  filas?: (colSpan: number) => ReactNode;
  /**
   * El desplegable cuando no hay filas que mostrar: un parrafo que explica
   * como se carga el primero. Es para el modelo que no esta en ningun lineup.
   */
  detalle?: ReactNode;
  /** El modelo esta marcado como «no lo trabajo»: va al fondo y atenuado. */
  apagado?: boolean;
  /**
   * Cuantos datos sin completar hay adentro, contando los de los equipos que
   * cuelgan. Sin esto, filtrar por incompletos mostraba los modelos pero no
   * decia cual abrir.
   */
  pendientes?: number;
  /** Una accion propia de la fila, a la derecha. */
  accion?: ReactNode;
}

export interface ListaProductosProps {
  productos: ReadonlyArray<Producto>;
  rubros: ReadonlyArray<Rubro>;
  /** Las marcas que existen en esta seccion, ya cruzadas con los otros filtros. */
  marcas: ReadonlyArray<string>;
  proveedores: ReadonlyArray<Proveedor>;
  filtros: FiltrosUi;
  onFiltros: (f: FiltrosUi) => void;
  onEditar: (p: Producto) => void;
  cargando?: boolean;
  /** Cuantos productos trajo el backend antes del filtro de texto. */
  totalCargados: number;
  /** El `PieDeLista`: cuantos hay en la base, si no entraron todos. */
  pie?: ReactNode;
  /** Lo que esta seccion le agrega a cada fila. */
  extras?: ExtrasDeFila;
  /** Controles propios de la seccion, al lado del contador de filas. */
  controles?: ReactNode;
  /**
   * Si la seccion agrupa por modelo, las filas son estos grupos y no los
   * productos. `productos` se sigue usando para contar y para el vacio: los
   * que se cuentan son los productos de verdad, no los grupos.
   */
  grupos?: ReadonlyArray<GrupoDeFilas>;
  /**
   * Cuántas fichas hay en los grupos (modelos y lo que cuelga). Es lo que cuenta
   * la solapa; sin nombrarlo, «Sellados 13» y «9 modelos» parecían decir cosas
   * distintas del mismo número.
   */
  fichasDeLosGrupos?: number;
  /**
   * Que decir cuando la seccion esta vacia y no hay NINGUN filtro puesto.
   * "No hay productos con estos filtros" seria mentira: no hay filtros; lo que
   * no hay todavia son productos, y cada seccion sabe como se carga el primero.
   */
  vacio?: string;
  /**
   * Un cartel entre los filtros y la tabla. Lo usa Usados para ofrecer ver
   * solo los equipos con datos sin completar: el numero sale de las mismas
   * fichas, asi que baja solo a medida que se completan.
   */
  aviso?: ReactNode;
}

/** Hay algo puesto que la persona puede querer sacar. */
function hayFiltros(f: FiltrosUi): boolean {
  return (
    f.texto.trim() !== "" ||
    f.rubroId !== "" ||
    f.marca !== "" ||
    f.proveedorId !== "" ||
    f.publicado !== "" ||
    f.activo !== FILTROS_INICIALES.activo
  );
}

export function ListaProductos({
  productos,
  rubros,
  marcas,
  proveedores,
  filtros,
  onFiltros,
  onEditar,
  cargando = false,
  totalCargados,
  pie,
  extras,
  vacio,
  aviso,
  controles,
  grupos,
  fichasDeLosGrupos,
}: ListaProductosProps): JSX.Element {
  const sinFilas = grupos ? grupos.length === 0 : productos.length === 0;
  /** Los grupos que se listan arriba y los que quedan plegados al fondo. */
  const gruposActivos = grupos?.filter((g) => !g.apagado);
  const gruposApagados = grupos?.filter((g) => g.apagado) ?? [];
  /**
   * Que fila tiene el detalle abierto. Una sola: dos detalles abiertos empujan
   * las filas de abajo el doble y se pierde el lugar que se estaba mirando.
   */
  const [abierta, setAbierta] = useState<number | null>(null);
  /** El grupo abierto, cuando la seccion agrupa por modelo. */
  const [abiertoGrupo, setAbiertoGrupo] = useState<string | null>(null);
  /**
   * Si se ven los modelos que el local no trabaja.
   *
   * Antes bajaban al fondo pero seguian listados, y eran doce filas apagadas
   * abajo de las que importan: en una seccion de cuarenta modelos, casi un
   * tercio de la lista era ruido permanente que habia que scrollear. Plegados
   * ocupan una linea y siguen a un clic de volver a prenderse, que es lo que
   * 0058 pedia: que no se escondan, no que esten siempre a la vista.
   */
  const [verApagados, setVerApagados] = useState(false);
  const alternar = (id: number): void => setAbierta((actual) => (actual === id ? null : id));
  const mensajeVacio = filtros.texto.trim()
    ? `No encontré «${filtros.texto.trim()}». La búsqueda mira el nombre y los dos códigos.`
    : hayFiltros(filtros) || !vacio
      ? "No hay productos con estos filtros."
      : vacio;

  const limpiar = hayFiltros(filtros) ? (
    <Button type="button" variant="outline" size="sm" onClick={() => onFiltros(FILTROS_INICIALES)}>
      Limpiar filtros
    </Button>
  ) : undefined;

  const nombreRubro = (id: number | null): string =>
    id === null ? "—" : (rubros.find((r) => r.id === id)?.nombre ?? "—");

  /**
   * Familia y proveedor solo se muestran si hay entre que elegir.
   *
   * Los dos reciben de arriba nada mas que lo que existe en la seccion, y a
   * veces eso es una sola cosa: la solapa Perfumes ES una familia, y Vapers
   * tiene un unico proveedor. Un desplegable con una sola opcion ademas de
   * «Todos» no puede cambiar el resultado; ocupa lugar y hace dudar al que lo
   * mira. Estado y Catalogo no entran en esta cuenta: sus opciones son fijas y
   * siempre dicen algo.
   */
  const conFiltroRubro = rubros.length > 1;
  const conFiltroMarca = marcas.length > 1;
  const conFiltroProveedor = proveedores.length > 1;
  const columnas =
    2 + (conFiltroRubro ? 1 : 0) + (conFiltroMarca ? 1 : 0) + (conFiltroProveedor ? 1 : 0);

  /**
   * La columna Familia se va con su filtro, por el mismo motivo: si en la
   * seccion hay una sola familia, la columna repite la misma palabra en cada
   * fila. En Usados decia "Usados" trescientas veces, y ese ancho es justo el
   * que le hace falta a la seña de cada equipo.
   */
  const conColumnaFamilia = conFiltroRubro;
  /**
   * La marca, con la misma regla. Se carga en la ficha y solo se veia en el
   * filtro: mirando todo el catalogo no habia forma de saber de que marca era
   * cada cosa, salvo que estuviera escrita en el nombre.
   */
  const conColumnaMarca = conFiltroMarca;
  /** Cuantas columnas tiene la tabla. La agrupada tiene su propio juego. */
  const colSpan = grupos
    ? 5 + (extras?.columnas?.length ?? 0)
    : 6 + (conColumnaFamilia ? 1 : 0) + (conColumnaMarca ? 1 : 0) + (extras?.columnas?.length ?? 0);

  /**
   * Una fila de grupo. Es una funcion y no JSX suelto porque se dibuja en dos
   * lugares: arriba los modelos que se trabajan y, plegados al fondo, los que
   * no.
   */
  const filaGrupo = (g: GrupoDeFilas): JSX.Element => {
    const abiertaEsta = abiertoGrupo === g.clave;
    return (
      <Fragment key={g.clave}>
        <TableRow
          desplegable
          abierta={abiertaEsta}
          onDesplegar={() => setAbiertoGrupo(abiertaEsta ? null : g.clave)}
          className={cn(g.apagado && "opacity-50")}
        >
          <TableCell className="max-w-[26rem]">
            <div className="flex min-w-0 items-center gap-2">
              <Chevron abierta={abiertaEsta} className="size-4 shrink-0 text-muted-foreground" />
              <span className="truncate text-sm font-medium">{g.titulo}</span>
              {g.pendientes ? (
                <Badge
                  variant="warning"
                  className="shrink-0 tabular-nums"
                  title={`${g.pendientes} dato(s) sin completar acá adentro`}
                >
                  <TriangleAlert aria-hidden="true" />
                  {g.pendientes}
                </Badge>
              ) : null}
            </div>
          </TableCell>
          {extras?.columnas?.map((c, i) => (
            <TableCell
              key={c.titulo}
              className={cn(
                "whitespace-nowrap text-sm",
                c.alineacion === "derecha" && "text-right tabular-nums",
              )}
            >
              {g.columnas?.[i]}
            </TableCell>
          ))}
          {/* El modelo no muestra plata: la plata es de la memoria. Mostraba
              un rango que resumia lo que las memorias dicen exacto un renglon
              mas abajo, y competia por la mirada con los numeros de verdad.
              Las dos celdas quedan vacias hasta que se despliega. */}
          <TableCellImporte />
          <TableCellImporte />
          <TableCellImporte>
            {/* Cero no se escribe: una columna con veinte "0,00 u."
      no deja ver los tres que si tienen algo. */}
            {g.stock > 0 ? (
              <Unidades cantidad={g.stock} />
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </TableCellImporte>
          <TableCell>
            <div className="flex items-center justify-end">{g.accion}</div>
          </TableCell>
        </TableRow>
        {abiertaEsta
          ? (g.filas?.(colSpan) ?? (
              <TableFilaDetalle colSpan={colSpan}>{g.detalle}</TableFilaDetalle>
            ))
          : null}
      </Fragment>
    );
  };

  return (
    <section className="space-y-3">
      <div className="flex flex-col gap-3">
        <InputBusqueda
          valor={filtros.texto}
          onValorChange={(texto) => onFiltros({ ...filtros, texto })}
          etiqueta="Buscar producto"
          placeholder="Buscar por nombre o código"
        />

        <div
          className={cn(
            "grid grid-cols-2 gap-2",
            columnas >= 5
              ? "lg:grid-cols-5"
              : columnas === 4
                ? "lg:grid-cols-4"
                : columnas === 3
                  ? "lg:grid-cols-3"
                  : "lg:grid-cols-2",
          )}
        >
          {conFiltroRubro ? (
            <FiltroSelect
              id="filtro-rubro"
              etiqueta="Familia"
              valor={filtros.rubroId}
              onChange={(v) => onFiltros({ ...filtros, rubroId: v })}
              placeholder="Todos"
              opciones={rubros.map((r) => ({ valor: String(r.id), label: r.nombre }))}
            />
          ) : null}
          {conFiltroMarca ? (
            <FiltroSelect
              id="filtro-marca"
              etiqueta="Marca"
              valor={filtros.marca}
              onChange={(v) => onFiltros({ ...filtros, marca: v })}
              placeholder="Todas"
              opciones={marcas.map((m) => ({ valor: m, label: m }))}
            />
          ) : null}
          {conFiltroProveedor ? (
            <FiltroSelect
              id="filtro-proveedor"
              etiqueta="Proveedor"
              valor={filtros.proveedorId}
              onChange={(v) => onFiltros({ ...filtros, proveedorId: v })}
              placeholder="Todos"
              opciones={proveedores.map((p) => ({ valor: String(p.id), label: p.nombre }))}
            />
          ) : null}
          <FiltroSelect
            id="filtro-activo"
            etiqueta="Estado"
            valor={filtros.activo}
            onChange={(v) => onFiltros({ ...filtros, activo: v === "no" ? "no" : "si" })}
            // El backend no ofrece "todos": sin filtro explicito devuelve los
            // activos. Se muestran las dos opciones reales en vez de una tercera
            // que mentiria.
            sinVacio
            opciones={[
              { valor: "si", label: "Activos" },
              { valor: "no", label: "Dados de baja" },
            ]}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {/* Con grupos se cuentan MODELOS: son las filas que se ven. Decir
                "11 productos" arriba de veinte filas es contar otra cosa. Y
                solo los que se trabajan: los apagados tienen su propio «N
                modelos que no trabajo» al fondo, y sumarlos aca decia «25
                modelos» arriba de nueve filas. */}
            {gruposActivos
              ? `${gruposActivos.length} ${gruposActivos.length === 1 ? "modelo" : "modelos"}${
                  fichasDeLosGrupos !== undefined
                    ? ` · ${fichasDeLosGrupos} ${fichasDeLosGrupos === 1 ? "ficha" : "fichas"}`
                    : ""
                }`
              : productos.length === totalCargados
                ? `${totalCargados} productos`
                : `${productos.length} de ${totalCargados} productos`}
          </p>
          {controles}
        </div>
      </div>

      {aviso}

      {/* Desktop: tabla compacta; en mobile son tarjetas. */}
      <Card padding="none" className="hidden overflow-hidden md:block">
        <Table densidad="compacta" alto="acotado">
          {/**
           * Los anchos, fijos, para la tabla agrupada.
           *
           * Sin esto las columnas se MOVIAN al desplegar un modelo. La fila del
           * modelo deja COSTO y PRECIO vacias —la plata es de la memoria— asi
           * que con el ancho automatico esas dos columnas valian lo que mide su
           * encabezado; al abrir aparecian cuatro filas con importes adentro,
           * el navegador repartia de nuevo el espacio y toda la tabla saltaba
           * de lugar. Justo lo contrario de lo que el desplegable vino a hacer:
           * que el renglon de abajo caiga debajo del encabezado de arriba.
           *
           * MODELO no lleva ancho: se queda con lo que sobra, que es lo que
           * tiene que pasar con la unica columna de texto libre. Su contenido
           * ya va con `truncate`.
           *
           * Son anchos PREFERIDOS y no `table-fixed`: asi el navegador puede
           * seguir achicandolos en una pantalla angosta en vez de dejar a
           * MODELO sin lugar. Lo unico que hacen falta es que las columnas de
           * plata reserven su espacio aunque la fila de arriba las deje
           * vacias, que era de donde salia el salto.
           */}
          {grupos ? (
            <colgroup>
              <col />
              {extras?.columnas?.map((c) => (
                <col key={c.titulo} className="w-28" />
              ))}
              <col className="w-40" />
              <col className="w-40" />
              <col className="w-20" />
              <col className="w-24" />
            </colgroup>
          ) : null}
          <TableHeader>
            <TableRow>
              <TableHead>{grupos ? "Modelo" : "Producto"}</TableHead>
              {/* Una tabla agrupada tiene MENOS columnas, no las mismas vacias.
                  Se van las dos que un modelo no tiene: el CODIGO (lo tiene
                  cada producto, no el modelo) y la FAMILIA (todos son iPhone).
                  El costo se queda: esto es administracion, y sin el no se ve
                  el margen, que es la razon de mirar el costo y el precio en la
                  misma fila. */}
              {grupos ? null : <TableHead>Código</TableHead>}
              {grupos || !conColumnaMarca ? null : <TableHead>Marca</TableHead>}
              {grupos || !conColumnaFamilia ? null : <TableHead>Familia</TableHead>}
              {extras?.columnas?.map((c) => (
                <TableHead
                  key={c.titulo}
                  className={cn("whitespace-nowrap", c.alineacion === "derecha" && "text-right")}
                >
                  {c.titulo}
                </TableHead>
              ))}
              <TableHead className="text-right">Costo</TableHead>
              <TableHead className="text-right">Precio</TableHead>
              <TableHead className="text-right">Stock</TableHead>
              <TableHead className={extras?.accion ? "w-20" : "w-10"}>
                <span className="sr-only">Acciones</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sinFilas ? (
              cargando ? (
                <tr>
                  <td colSpan={colSpan}>
                    <Cargando texto="Buscando…" />
                  </td>
                </tr>
              ) : (
                <TableVacia colSpan={colSpan} mensaje={mensajeVacio} accion={limpiar} />
              )
            ) : grupos ? (
              <>
                {gruposActivos?.map(filaGrupo)}
                {gruposApagados.length > 0 ? (
                  <>
                    <tr>
                      <td colSpan={colSpan} className="p-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-expanded={verApagados}
                          onClick={() => setVerApagados((v) => !v)}
                          className="w-full justify-start text-muted-foreground"
                        >
                          {gruposApagados.length === 1
                            ? "1 modelo que no trabajo"
                            : `${gruposApagados.length} modelos que no trabajo`}
                        </Button>
                      </td>
                    </tr>
                    {verApagados ? gruposApagados.map(filaGrupo) : null}
                  </>
                ) : null}
              </>
            ) : (
              productos.map((p) => {
                const detalle = extras?.detalle?.(p);
                const estaAbierta = abierta === p.id;
                return (
                  <Fragment key={p.id}>
                    <TableRow
                      desplegable={Boolean(detalle)}
                      abierta={estaAbierta}
                      onDesplegar={() => alternar(p.id)}
                      className={cn(extras?.apagada?.(p) && "opacity-60")}
                    >
                      <TableCell className="max-w-[26rem]">
                        <div className="flex min-w-0 items-center gap-2">
                          {detalle ? (
                            <Chevron
                              abierta={estaAbierta}
                              className="size-4 shrink-0 text-muted-foreground"
                            />
                          ) : null}
                          <span className="truncate text-sm">{p.nombre}</span>
                          <Marcas producto={p} extras={extras} />
                          {extras?.chips?.(p)}
                          <Pendientes producto={p} extras={extras} />
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        <Codigo>
                          {p.codigo_interno}
                          {p.codigo_externo ? ` · ${p.codigo_externo}` : ""}
                        </Codigo>
                      </TableCell>
                      {conColumnaMarca ? (
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                          {p.marca ?? "—"}
                        </TableCell>
                      ) : null}
                      {conColumnaFamilia ? (
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                          {nombreRubro(p.rubro_id)}
                        </TableCell>
                      ) : null}
                      {extras?.columnas?.map((c) => (
                        <TableCell
                          key={c.titulo}
                          className={cn(
                            "whitespace-nowrap text-sm",
                            c.alineacion === "derecha" && "text-right tabular-nums",
                          )}
                        >
                          {c.celda(p)}
                        </TableCell>
                      ))}
                      {/* El chip va SIEMPRE en estas dos columnas: mezclan productos
                          en pesos y en dolares, asi que el encabezado no puede
                          declarar la moneda (MASTER seccion 3.4). */}
                      <TableCellImporte monto={aNumero(p.costo)} moneda={p.costo_moneda} />
                      <TableCellImporte monto={aNumero(p.precio_venta)} moneda={p.precio_moneda} />
                      <TableCellImporte>
                        <Unidades cantidad={extras?.stock?.(p) ?? aNumero(p.stock_actual)} />
                      </TableCellImporte>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          {extras?.accion?.(p)}
                          {/* Editar no es desplegar: el clic se queda en el boton. */}
                          <IconButton
                            icono={Pencil}
                            tamano="sm"
                            aria-label={`Editar ${nombreCompleto(p)}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditar(p);
                            }}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                    {estaAbierta && detalle ? (
                      <TableFilaDetalle colSpan={colSpan}>{detalle}</TableFilaDetalle>
                    ) : null}
                  </Fragment>
                );
              })
            )}
            {productos.length > 0 && pie ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={colSpan}>{pie}</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Card>

      {/* Mobile: una tarjeta-registro por producto. */}
      <div className="flex flex-col gap-2 md:hidden">
        {sinFilas ? (
          cargando ? (
            <Cargando texto="Buscando…" />
          ) : (
            <Card padding="none">
              <EstadoVacio tamano="sm" titulo={mensajeVacio} accion={limpiar} />
            </Card>
          )
        ) : (
          <>
            {gruposActivos?.map((g) => (
              <TarjetaGrupo
                key={g.clave}
                grupo={g}
                abierta={abiertoGrupo === g.clave}
                onDesplegar={() => setAbiertoGrupo(abiertoGrupo === g.clave ? null : g.clave)}
              />
            ))}
            {gruposApagados.length > 0 ? (
              <>
                <Button
                  type="button"
                  variant="dashed"
                  size="sm"
                  aria-expanded={verApagados}
                  onClick={() => setVerApagados((v) => !v)}
                  className="justify-start"
                >
                  {gruposApagados.length === 1
                    ? "1 modelo que no trabajo"
                    : `${gruposApagados.length} modelos que no trabajo`}
                </Button>
                {verApagados
                  ? gruposApagados.map((g) => (
                      <TarjetaGrupo
                        key={g.clave}
                        grupo={g}
                        abierta={abiertoGrupo === g.clave}
                        onDesplegar={() =>
                          setAbiertoGrupo(abiertoGrupo === g.clave ? null : g.clave)
                        }
                      />
                    ))
                  : null}
              </>
            ) : null}
            {grupos
              ? null
              : productos.map((p) => (
                  <TarjetaProducto
                    key={p.id}
                    producto={p}
                    onEditar={onEditar}
                    extras={extras}
                    abierta={abierta === p.id}
                    onDesplegar={() => alternar(p.id)}
                  />
                ))}
            {pie}
          </>
        )}
      </div>
    </section>
  );
}

/**
 * Tarjeta-registro (06 seccion 3.4): nombre y codigo a la izquierda, el precio
 * arriba a la derecha, las marcas y el stock abajo. La barra lateral es la
 * moneda del PRECIO, que es el numero grande de la tarjeta.
 */
function TarjetaProducto({
  producto: p,
  onEditar,
  extras,
  abierta,
  onDesplegar,
}: {
  producto: Producto;
  onEditar: (p: Producto) => void;
  extras?: ExtrasDeFila;
  abierta: boolean;
  onDesplegar: () => void;
}): JSX.Element {
  const detalle = extras?.detalle?.(p);
  return (
    <Card
      as="article"
      padding="compacto"
      acento={p.precio_moneda}
      className={cn("flex flex-col gap-2", extras?.apagada?.(p) && "opacity-60")}
    >
      {/* Toda la tarjeta abre el detalle; el lapiz frena el toque. */}
      <div
        className={cn("flex flex-col gap-2", detalle && "cursor-pointer")}
        {...(detalle
          ? {
              role: "button",
              tabIndex: 0,
              "aria-expanded": abierta,
              onClick: onDesplegar,
              onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                onDesplegar();
              },
            }
          : {})}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2">
            {detalle ? (
              <Chevron abierta={abierta} className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            ) : null}
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{p.nombre}</p>
              <p className="truncate text-xs text-muted-foreground">
                {p.marca ? `${p.marca} · ` : null}
                <Codigo>
                  {p.codigo_interno}
                  {p.codigo_externo ? ` · ${p.codigo_externo}` : ""}
                </Codigo>
              </p>
            </div>
          </div>
          <Importe monto={aNumero(p.precio_venta)} moneda={p.precio_moneda} className="shrink-0" />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex flex-wrap items-center gap-2">
            <Marcas producto={p} extras={extras} />
            {extras?.chips?.(p)}
            <Pendientes producto={p} extras={extras} />
            <Unidades cantidad={extras?.stock?.(p) ?? aNumero(p.stock_actual)} />
          </span>
          <span className="flex items-center gap-2">
            {extras?.columnas?.map((c) => (
              <span key={c.titulo} className="text-xs text-muted-foreground">
                {c.titulo} <span className="text-foreground">{c.celda(p)}</span>
              </span>
            ))}
            <span className="text-xs text-muted-foreground">
              Costo <Importe monto={aNumero(p.costo)} moneda={p.costo_moneda} tamano="sm" />
            </span>
            <IconButton
              icono={Pencil}
              tamano="sm"
              aria-label={`Editar ${nombreCompleto(p)}`}
              onClick={(e) => {
                e.stopPropagation();
                onEditar(p);
              }}
            />
          </span>
        </div>
      </div>

      {abierta && detalle ? <div className="border-t border-border pt-2">{detalle}</div> : null}
    </Card>
  );
}

/** La señal de que la fila tiene algo abajo. No es un control: la fila lo es. */
function Chevron({ abierta, className }: { abierta: boolean; className?: string }): JSX.Element {
  const Icono = abierta ? ChevronDown : ChevronRight;
  return <Icono className={className} aria-hidden="true" />;
}

/** El stock negativo se marca: es legitimo, pero hay que verlo. */
function Unidades({ cantidad }: { cantidad: number }): JSX.Element {
  return (
    <span
      className={cn(
        "whitespace-nowrap text-sm tabular-nums",
        cantidad < 0
          ? "font-medium text-destructive"
          : cantidad === 0
            ? "text-muted-foreground"
            : "",
      )}
    >
      {mostrarCantidad(cantidad)} u.
    </span>
  );
}

export function Marcas({
  producto,
  extras,
}: {
  producto: Producto;
  extras?: ExtrasDeFila;
}): JSX.Element {
  const visibles = extras?.marcas?.(producto) ?? marcasDe(producto);
  return (
    <span className="flex shrink-0 flex-wrap items-center gap-1">
      {visibles.map((m) => (
        <Badge key={m} variant={TONO_ESTADO[m]}>
          {m === "precioManual" ? <PencilRuler aria-hidden="true" /> : null}
          {ETIQUETA_MARCA[m]}
        </Badge>
      ))}
    </span>
  );
}

function FiltroSelect({
  id,
  etiqueta,
  valor,
  onChange,
  opciones,
  placeholder,
  sinVacio = false,
}: {
  id: string;
  etiqueta: string;
  valor: string;
  onChange: (v: string) => void;
  opciones: ReadonlyArray<{ valor: string; label: string }>;
  placeholder?: string;
  sinVacio?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {etiqueta}
      </Label>
      <Select value={valor} onValueChange={(v) => onChange(v === "_" ? "" : v)}>
        <SelectTrigger id={id} aria-label={etiqueta}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {sinVacio ? null : <SelectItem value="_">{placeholder ?? "Todos"}</SelectItem>}
          {opciones.map((o) => (
            <SelectItem key={o.valor} value={o.valor}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Cuantos datos le faltan a la ficha, en un solo chip.
 *
 * El numero y no las palabras: «Falta IMEI» y «Sin precio» uno al lado del
 * otro ocupan lo que mide el nombre del producto, y lo que hace falta saber
 * recorriendo la lista es que a esta fila le falta algo. Que le falta lo dice
 * el detalle, que esta a un clic.
 *
 * El nombre accesible si lo enumera: un lector de pantalla no puede abrir la
 * fila para enterarse.
 */
export function Pendientes({
  producto,
  extras,
}: {
  producto: Producto;
  extras?: ExtrasDeFila;
}): JSX.Element | null {
  const faltan = extras?.pendientes?.(producto) ?? [];
  if (faltan.length === 0) return null;
  return (
    <Badge variant="warning" className="shrink-0 tabular-nums" title={faltan.join(" · ")}>
      <TriangleAlert aria-hidden="true" />
      {faltan.length}
      <span className="sr-only">
        {faltan.length === 1 ? "dato sin completar" : "datos sin completar"}: {faltan.join(", ")}
      </span>
    </Badge>
  );
}

/**
 * La tarjeta de un modelo en mobile. Misma idea que la fila: el titulo, el
 * resumen, cuantas hay, y lo que se abre al tocarla.
 */
function TarjetaGrupo({
  grupo,
  abierta,
  onDesplegar,
}: {
  grupo: GrupoDeFilas;
  abierta: boolean;
  onDesplegar: () => void;
}): JSX.Element {
  return (
    <Card
      as="article"
      padding="compacto"
      className={cn("flex flex-col gap-2", grupo.apagado && "opacity-50")}
    >
      <div
        role="button"
        tabIndex={0}
        aria-expanded={abierta}
        className="flex cursor-pointer flex-col gap-1"
        onClick={onDesplegar}
        onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          onDesplegar();
        }}
      >
        <div className="flex items-center gap-2">
          <Chevron abierta={abierta} className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{grupo.titulo}</span>
          {grupo.pendientes ? (
            <Badge variant="warning" className="shrink-0 tabular-nums">
              <TriangleAlert aria-hidden="true" />
              {grupo.pendientes}
            </Badge>
          ) : null}
          <Unidades cantidad={grupo.stock} />
          {grupo.accion}
        </div>
        {/* La tarjeta no tiene columnas: todo va en una linea. La plata no
            entra por lo mismo que en la tabla: es de la memoria. */}
        <p className="flex flex-wrap items-center gap-2 pl-6 text-xs text-muted-foreground">
          {grupo.columnas}
        </p>
      </div>
      {abierta ? <div className="border-t border-border pt-2">{grupo.detalle}</div> : null}
    </Card>
  );
}
